import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Fade,
  Slide,
  Container,
  Chip,
  Paper,
  Avatar,
} from '@mui/material';
import {
  SmartToy,
  Computer,
  Security,
  Speed,
  Analytics,
  Code,
  BugReport,
  Timeline,
  PlayArrow,
} from '@mui/icons-material';
import { Project } from '../types';
import { projectsAPI } from '../services/api';
import ProjectList from './ProjectList';
import RobotDashboard from './RobotDashboard';
import ServerDashboard from './ServerDashboard';
import LogoutButton from './LogoutButton';
// Lazy load heavy components
import {
  ProjectDetails,
  ParametersView,
  CurrentChangesView,
  ChangedFilesList,
  ApplicationFoldersList,
  FolderGitHistory,
} from './LazyComponents';
import SystemParametersGitHistory from './SystemParametersGitHistoryNew';

interface MainDashboardProps {
  onLogout: () => void;
}

const MainDashboard: React.FC<MainDashboardProps> = ({ onLogout }) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [currentView, setCurrentView] = useState<'categories' | 'projects' | 'details' | 'robotDashboard' | 'serverDashboard' | 'parameters' | 'currentChanges' | 'changedFiles' | 'applicationFolders' | 'folderGitHistory' | 'systemParametersGitHistory'>('categories');
  const [selectedCategory, setSelectedCategory] = useState<'robots' | 'servers' | null>(null);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [selectedFolder, setSelectedFolder] = useState<string | null>(null);
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);
  const [previousView, setPreviousView] = useState<string | null>(null);

  // Enhanced logout function that clears URL parameters
  const handleLogout = () => {
    // Clear all URL parameters
    setSearchParams({});
    // Clear session storage
    sessionStorage.removeItem('freshLogin');
    // Call the original logout function
    onLogout();
  };

  // Animated particles effect
  const [particles, setParticles] = useState<Array<{ id: number; x: number; y: number; size: number; speed: number }>>([]);

  useEffect(() => {
    // Generate floating particles
    const generateParticles = () => {
      const newParticles = Array.from({ length: 20 }, (_, i) => ({
        id: i,
        x: Math.random() * 100,
        y: Math.random() * 100,
        size: Math.random() * 4 + 2,
        speed: Math.random() * 0.5 + 0.2,
      }));
      setParticles(newParticles);
    };

    generateParticles();
  }, []);

  // Handle URL parameters on mount and refresh
  useEffect(() => {
    // Check if this is a fresh login by looking for a specific flag in sessionStorage
    const isFreshLogin = sessionStorage.getItem('freshLogin') === 'true';
    console.log('MainDashboard useEffect - isFreshLogin:', isFreshLogin);

    if (isFreshLogin) {
      // Fresh login - always start from categories view
      console.log('Fresh login detected - resetting to categories');
      setCurrentView('categories');
      setSelectedCategory(null);
      setSelectedProject(null);
      setPreviousView(null);

      // Clear URL parameters and remove the fresh login flag
      setSearchParams({});
      sessionStorage.removeItem('freshLogin');
    } else {
      // Page refresh or normal navigation - restore state from URL parameters
      const view = searchParams.get('view') || 'categories';
      const category = searchParams.get('category') as 'robots' | 'servers' | null;
      const projectName = searchParams.get('project');
      const projectType = searchParams.get('projectType') as 'robot' | 'server' | null;
      const prevView = searchParams.get('prevView');

      if (view !== 'categories') {
        setCurrentView(view as any);
      }

      if (category) {
        setSelectedCategory(category);
      }

      if (projectName && projectType) {
        // Fetch the full project data from API to get robotNames and other details
        const fetchProjectData = async () => {
          try {
            const isRobot = projectType === 'robot';
            const response = await projectsAPI.getProjects(isRobot);

            if (response.success) {
              // Find the project that matches the projectName
              const project = response.data.find((p: Project) => p.projectName === projectName);

              if (project) {
                setSelectedProject(project);
              } else {
                // Fallback to mock project if not found
                const mockProject: Project = {
                  _id: projectName,
                  projectName: projectName,
                  isRobot: isRobot,
                  ipAddress: searchParams.get('ipAddress') || undefined,
                  robotNames: [],
                  latestCommit: {
                    commitHash: '',
                    commitMessage: '',
                    authorName: '',
                    commitTimestamp: '',
                    branchName: ''
                  },
                  totalCommits: 0,
                  lastActivity: ''
                };
                setSelectedProject(mockProject);
              }
            }
          } catch (error) {
            console.error('Error fetching project data:', error);
            // Fallback to mock project on error
            const mockProject: Project = {
              _id: projectName,
              projectName: projectName,
              isRobot: projectType === 'robot',
              ipAddress: searchParams.get('ipAddress') || undefined,
              robotNames: [],
              latestCommit: {
                commitHash: '',
                commitMessage: '',
                authorName: '',
                commitTimestamp: '',
                branchName: ''
              },
              totalCommits: 0,
              lastActivity: ''
            };
            setSelectedProject(mockProject);
          }
        };

        fetchProjectData();
      }

      if (prevView) {
        setPreviousView(prevView);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount, not on searchParams changes

  // Helper function to update URL parameters
  const updateURL = (params: {
    view?: string;
    category?: string;
    project?: string;
    projectType?: string;
    prevView?: string;
    ipAddress?: string;
  }) => {
    const newParams = new URLSearchParams(searchParams);

    if (params.view !== undefined) {
      if (params.view === 'categories') {
        newParams.delete('view');
        newParams.delete('category');
        newParams.delete('project');
        newParams.delete('projectType');
        newParams.delete('prevView');
        newParams.delete('ipAddress');
      } else {
        newParams.set('view', params.view);
      }
    }

    if (params.category !== undefined) {
      if (params.category) {
        newParams.set('category', params.category);
      } else {
        newParams.delete('category');
      }
    }

    if (params.project !== undefined) {
      if (params.project) {
        newParams.set('project', params.project);
      } else {
        newParams.delete('project');
      }
    }

    if (params.projectType !== undefined) {
      if (params.projectType) {
        newParams.set('projectType', params.projectType);
      } else {
        newParams.delete('projectType');
      }
    }

    if (params.prevView !== undefined) {
      if (params.prevView) {
        newParams.set('prevView', params.prevView);
      } else {
        newParams.delete('prevView');
      }
    }

    if (params.ipAddress !== undefined) {
      if (params.ipAddress) {
        newParams.set('ipAddress', params.ipAddress);
      } else {
        newParams.delete('ipAddress');
      }
    }

    setSearchParams(newParams);
  };

  const handleCategorySelect = (category: 'robots' | 'servers') => {
    console.log(`Category selected: ${category}`);
    setSelectedCategory(category);
    setCurrentView('projects');
    updateURL({ view: 'projects', category });
  };

  const handleProjectSelect = (project: Project) => {
    setSelectedProject(project);

    // Navigate to appropriate dashboard based on category
    if (selectedCategory === 'robots') {
      setPreviousView('robotOptions');
      setCurrentView('robotDashboard');
      updateURL({
        view: 'robotDashboard',
        category: selectedCategory,
        project: project.projectName,
        projectType: 'robot',
        prevView: 'robotOptions',
        ipAddress: project.ipAddress
      });
    } else if (selectedCategory === 'servers') {
      setPreviousView('serverOptions');
      setCurrentView('serverDashboard');
      updateURL({
        view: 'serverDashboard',
        category: selectedCategory,
        project: project.projectName,
        projectType: 'server',
        prevView: 'serverOptions',
        ipAddress: project.ipAddress
      });
    }
  };

  // Navigation handlers for new dashboards
  const handleBackToRobotOptions = () => {
    setCurrentView('projects');
    setSelectedProject(null);
    setPreviousView(null);
    updateURL({
      view: 'projects',
      category: 'robots'
    });
  };

  const handleBackToServerOptions = () => {
    setCurrentView('projects');
    setSelectedProject(null);
    setPreviousView(null);
    updateURL({
      view: 'projects',
      category: 'servers'
    });
  };

  // New functions for navigating back to dashboards from sub-pages
  const handleBackToRobotDashboard = () => {
    setCurrentView('robotDashboard');
    setPreviousView('robotOptions');
    updateURL({
      view: 'robotDashboard',
      category: 'robots',
      project: selectedProject?.projectName,
      projectType: 'robot',
      prevView: 'robotOptions',
      ipAddress: selectedProject?.ipAddress
    });
  };

  const handleBackToServerDashboard = () => {
    setCurrentView('serverDashboard');
    setPreviousView('serverOptions');
    updateURL({
      view: 'serverDashboard',
      category: 'servers',
      project: selectedProject?.projectName,
      projectType: 'server',
      prevView: 'serverOptions',
      ipAddress: selectedProject?.ipAddress
    });
  };

  const handleNavigateToParameters = () => {
    setPreviousView(selectedCategory === 'robots' ? 'robotOptions' : 'serverOptions');
    setCurrentView('parameters');
    updateURL({
      view: 'parameters',
      category: selectedCategory || undefined,
      project: selectedProject?.projectName,
      projectType: selectedCategory === 'robots' ? 'robot' : 'server',
      prevView: selectedCategory === 'robots' ? 'robotOptions' : 'serverOptions'
    });
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleNavigateToChangedFiles = () => {
    setPreviousView(selectedCategory === 'robots' ? 'robotOptions' : 'serverOptions');
    setCurrentView('changedFiles');
    updateURL({
      view: 'changedFiles',
      category: selectedCategory || undefined,
      project: selectedProject?.projectName,
      projectType: selectedCategory === 'robots' ? 'robot' : 'server',
      prevView: selectedCategory === 'robots' ? 'robotOptions' : 'serverOptions'
    });
  };

  const handleNavigateToCurrentChanges = () => {
    setPreviousView(selectedCategory === 'robots' ? 'robotOptions' : 'serverOptions');
    setCurrentView('currentChanges');
    updateURL({
      view: 'currentChanges',
      category: selectedCategory || undefined,
      project: selectedProject?.projectName,
      projectType: selectedCategory === 'robots' ? 'robot' : 'server',
      prevView: selectedCategory === 'robots' ? 'robotOptions' : 'serverOptions'
    });
  };

  const handleNavigateToApplicationFolders = () => {
    setPreviousView(selectedCategory === 'robots' ? 'robotOptions' : 'serverOptions');
    setCurrentView('applicationFolders');
    updateURL({
      view: 'applicationFolders',
      category: selectedCategory || undefined,
      project: selectedProject?.projectName,
      projectType: selectedCategory === 'robots' ? 'robot' : 'server',
      prevView: selectedCategory === 'robots' ? 'robotOptions' : 'serverOptions'
    });
  };

  const handleFolderSelect = (folderName: string, isProject?: boolean) => {
    if (isProject) {
      // For projects, navigate to project details view
      setSelectedProject({
        ...selectedProject!,
        projectName: folderName
      });
      setPreviousView('applicationFolders');
      setCurrentView('details');
      updateURL({
        view: 'details',
        category: selectedCategory || undefined,
        project: folderName,
        projectType: selectedCategory === 'robots' ? 'robot' : 'server'
      });
    } else {
      // For folders, navigate to folder git history
      setSelectedFolder(folderName);
      setPreviousView('applicationFolders');
      setCurrentView('folderGitHistory');
      updateURL({
        view: 'folderGitHistory',
        category: selectedCategory || undefined,
        project: selectedProject?.projectName,
        projectType: selectedCategory === 'robots' ? 'robot' : 'server'
      });
    }
  };

  const handleBackToApplicationFolders = () => {
    // Go back to the appropriate dashboard based on project type
    if (selectedProject?.isRobot) {
      setCurrentView('robotDashboard');
      setPreviousView('robotOptions');
    } else {
      setCurrentView('serverDashboard');
      setPreviousView('serverOptions');
    }
    setSelectedFolder(null);
    updateURL({
      view: selectedProject?.isRobot ? 'robotDashboard' : 'serverDashboard',
      category: selectedCategory || undefined,
      project: selectedProject?.projectName,
      projectType: selectedProject?.isRobot ? 'robot' : 'server',
      prevView: selectedProject?.isRobot ? 'robotOptions' : 'serverOptions',
      ipAddress: selectedProject?.ipAddress
    });
  };

  const handleNavigateToSystemParametersGit = () => {
    setPreviousView('robotOptions');
    setCurrentView('systemParametersGitHistory');
    updateURL({
      view: 'systemParametersGitHistory',
      category: selectedCategory || undefined,
      project: selectedProject?.projectName,
      projectType: selectedCategory === 'robots' ? 'robot' : 'server',
      prevView: 'robotOptions'
    });
  };

  const handleBackToCategories = () => {
    setCurrentView('categories');
    setSelectedCategory(null);
    setSelectedProject(null);
    setPreviousView(null);
    updateURL({ view: 'categories' });
  };

  const handleBackToProjects = () => {
    setCurrentView('projects');
    setSelectedProject(null);
    updateURL({
      view: 'projects',
      project: undefined,
      projectType: undefined,
      prevView: undefined
    });
  };


  const getBackHandler = () => {
    if (previousView === 'robotOptions') {
      return handleBackToRobotDashboard;
    } else if (previousView === 'serverOptions') {
      return handleBackToServerDashboard;
    } else {
      return handleBackToProjects;
    }
  };

  const floatingElements = useMemo(() => (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        overflow: 'hidden',
        pointerEvents: 'none',
        zIndex: 1,
      }}
    >
      {/* Floating particles */}
      {particles.map((particle) => (
        <Box
          key={particle.id}
          sx={{
            position: 'absolute',
            left: `${particle.x}%`,
            top: `${particle.y}%`,
            width: particle.size,
            height: particle.size,
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.1)',
            animation: `float ${3 + particle.speed}s ease-in-out infinite`,
            animationDelay: `${particle.speed}s`,
          }}
        />
      ))}

      {/* Floating icons */}
      {[
        { icon: <Code />, x: 10, y: 20, delay: 0 },
        { icon: <Analytics />, x: 85, y: 15, delay: 1 },
        { icon: <Security />, x: 15, y: 70, delay: 2 },
        { icon: <Speed />, x: 80, y: 75, delay: 1.5 },
        { icon: <BugReport />, x: 5, y: 45, delay: 0.5 },
        { icon: <Timeline />, x: 90, y: 50, delay: 2.5 },
      ].map((item, index) => (
        <Box
          key={index}
          sx={{
            position: 'absolute',
            left: `${item.x}%`,
            top: `${item.y}%`,
            color: 'rgba(255, 255, 255, 0.1)',
            animation: `floatIcon ${4 + item.delay}s ease-in-out infinite`,
            animationDelay: `${item.delay}s`,
          }}
        >
          {item.icon}
        </Box>
      ))}

      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes float {
            0%, 100% { transform: translateY(0px) rotate(0deg); }
            50% { transform: translateY(-20px) rotate(180deg); }
          }
          @keyframes floatIcon {
            0%, 100% { transform: translateY(0px) scale(1); }
            50% { transform: translateY(-15px) scale(1.1); }
          }
        `
      }} />
    </Box>
  ), [particles]);

  const renderFeatureCard = (
    title: string,
    description: string,
    icon: React.ReactNode,
    gradient: string,
    onClick: () => void,
    delay: number,
    category: 'robots' | 'servers'
  ) => (
    <Slide direction="up" in timeout={800 + delay}>
      <Card
        sx={{
          background: gradient,
          backdropFilter: 'blur(20px)',
          color: '#334155',
          cursor: 'pointer',
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-12px) scale(1.02)',
            boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
          },
          borderRadius: 4,
          overflow: 'hidden',
          position: 'relative',
          minHeight: 300,
          border: '1px solid rgba(255, 255, 255, 0.2)',
          boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
        }}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          console.log(`Card clicked: ${category}`);
          onClick();
        }}
        onMouseEnter={() => setHoveredCard(category)}
        onMouseLeave={() => setHoveredCard(null)}
      >
        <CardContent sx={{ position: 'relative', zIndex: 2, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 4 }}>
          {/* Header */}
          <Box sx={{ textAlign: 'center' }}>
            <Avatar
              sx={{
                width: 100,
                height: 100,
                mx: 'auto',
                mb: 3,
                background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                backdropFilter: 'blur(20px)',
                border: '2px solid rgba(139, 92, 246, 0.3)',
                boxShadow: '0 8px 32px rgba(139, 92, 246, 0.3)',
                transform: hoveredCard === category ? 'scale(1.1) rotate(5deg)' : 'scale(1)',
                transition: 'all 0.3s ease',
              }}
            >
              <Box sx={{ color: 'white' }}>
                {icon}
              </Box>
            </Avatar>
            <Typography variant="h3" fontWeight="bold" mb={2}>
              {title}
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.9, mb: 4, maxWidth: 400 }}>
              {description}
            </Typography>

            {/* Action Button */}
            <Chip
              icon={<PlayArrow />}
              label="Explore"
              sx={{
                background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                color: 'white',
                border: '1px solid rgba(139, 92, 246, 0.3)',
                px: 3,
                py: 1.5,
                fontSize: '1rem',
                height: 'auto',
                boxShadow: '0 4px 16px rgba(139, 92, 246, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
                  transform: 'scale(1.05)',
                  boxShadow: '0 6px 20px rgba(139, 92, 246, 0.4)',
                },
                transition: 'all 0.2s ease',
              }}
            />
          </Box>
        </CardContent>
      </Card>
    </Slide>
  );

  const renderCategories = () => (
    <Fade in timeout={500}>
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #1a2027 0%, #2d3748 100%)',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `
              radial-gradient(circle at 20% 80%, rgba(139, 92, 246, 0.3) 0%, transparent 50%),
              radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
              radial-gradient(circle at 40% 40%, rgba(139, 92, 246, 0.2) 0%, transparent 50%)
            `,
            zIndex: 1,
          },
        }}
      >
        {floatingElements}

        {/* Top Right Logout Button */}
        <LogoutButton onLogout={handleLogout} />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2, py: 6 }}>
          {/* Header */}
          <Fade in timeout={800}>
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Typography
                variant="h2"
                fontWeight="bold"
                sx={{
                  mb: 2,
                  color: 'white',
                  textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                }}
              >
                Ottonomy Deployments Tracking Dashboard
              </Typography>
              <Typography variant="h5" color="rgba(255,255,255,0.9)" sx={{ maxWidth: 600, mx: 'auto', mb: 4 }}>
                Choose the category to explore the status of robot and server deployments
              </Typography>
            </Box>
          </Fade>

          {/* Main Category Cards */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, 1fr)' },
              gap: 4,
              maxWidth: 1200,
              mx: 'auto',
              mb: 6,
            }}
          >
            {renderFeatureCard(
              'Ottobots',
              'Parameter and Code Versioning check for Ottobots',
              <SmartToy sx={{ fontSize: 50 }} />,
              'rgba(255, 255, 255, 0.95)',
              () => handleCategorySelect('robots'),
              0,
              'robots'
            )}

            {renderFeatureCard(
              'Servers',
              'Parameter and Code Versioning check for Servers',
              <Computer sx={{ fontSize: 50 }} />,
              'rgba(255, 255, 255, 0.95)',
              () => handleCategorySelect('servers'),
              200,
              'servers'
            )}
          </Box>
        </Container>
      </Box>
    </Fade>
  );

  if (currentView === 'categories') {
    return renderCategories();
  }

  if (currentView === 'projects' && selectedCategory) {
    return (
      <Box sx={{ position: 'relative', minHeight: '100vh' }}>
        <LogoutButton onLogout={handleLogout} />
        <ProjectList
          category={selectedCategory}
          onProjectSelect={handleProjectSelect}
          onBack={handleBackToCategories}
        />
      </Box>
    );
  }

  if (currentView === 'details' && selectedProject) {
    return (
      <Box sx={{ position: 'relative', minHeight: '100vh' }}>
        <LogoutButton onLogout={handleLogout} />
        <ProjectDetails
          project={selectedProject}
          onBack={getBackHandler()}
        />
      </Box>
    );
  }

  if (currentView === 'robotDashboard' && selectedProject) {
    return (
      <Box sx={{ position: 'relative', minHeight: '100vh', background: 'linear-gradient(135deg, #1a2027 0%, #2d3748 100%)' }}>
        <LogoutButton onLogout={handleLogout} />
        <RobotDashboard
          robot={selectedProject}
          onBack={handleBackToRobotOptions}
          onNavigateToParameters={handleNavigateToParameters}
          onNavigateToSystemParametersGit={handleNavigateToSystemParametersGit}
          onNavigateToApplicationFolders={handleNavigateToApplicationFolders}
          onNavigateToCurrentChanges={handleNavigateToCurrentChanges}
        />
      </Box>
    );
  }

  if (currentView === 'serverDashboard' && selectedProject) {
    return (
      <Box sx={{ position: 'relative', minHeight: '100vh', background: 'linear-gradient(135deg, #1a2027 0%, #2d3748 100%)' }}>
        <LogoutButton onLogout={handleLogout} />
        <ServerDashboard
          server={selectedProject}
          onBack={handleBackToServerOptions}
          onNavigateToParameters={handleNavigateToParameters}
          onNavigateToApplicationFolders={handleNavigateToApplicationFolders}
          onNavigateToCurrentChanges={handleNavigateToCurrentChanges}
        />
      </Box>
    );
  }

  if (currentView === 'parameters' && selectedProject) {
    return (
      <Box sx={{ position: 'relative', minHeight: '100vh' }}>
        <LogoutButton onLogout={handleLogout} />
        <ParametersView
          project={selectedProject}
          onBack={() => {
            // Go back to the appropriate dashboard based on prevView
            if (previousView === 'robotOptions') {
              setCurrentView('robotDashboard');
              updateURL({
                view: 'robotDashboard',
                category: selectedCategory || undefined,
                project: selectedProject.projectName,
                projectType: 'robot',
                prevView: 'robotOptions',
                ipAddress: selectedProject.ipAddress
              });
            } else if (previousView === 'serverOptions') {
              setCurrentView('serverDashboard');
              updateURL({
                view: 'serverDashboard',
                category: selectedCategory || undefined,
                project: selectedProject.projectName,
                projectType: 'server',
                prevView: 'serverOptions',
                ipAddress: selectedProject.ipAddress
              });
            } else {
              // Fallback to projects list
              setCurrentView('projects');
              updateURL({ view: 'projects', category: selectedCategory || undefined });
            }
          }}
        />
      </Box>
    );
  }

  if (currentView === 'currentChanges' && selectedProject) {
    return (
      <Box sx={{ position: 'relative', minHeight: '100vh' }}>
        <LogoutButton onLogout={handleLogout} />
        <CurrentChangesView
          project={selectedProject}
          ipAddress={selectedProject.ipAddress}
          onBack={() => {
            // Go back to the appropriate dashboard based on prevView
            if (previousView === 'robotOptions') {
              setCurrentView('robotDashboard');
              updateURL({
                view: 'robotDashboard',
                category: selectedCategory || undefined,
                project: selectedProject.projectName,
                projectType: 'robot',
                prevView: 'robotOptions',
                ipAddress: selectedProject.ipAddress
              });
            } else if (previousView === 'serverOptions') {
              setCurrentView('serverDashboard');
              updateURL({
                view: 'serverDashboard',
                category: selectedCategory || undefined,
                project: selectedProject.projectName,
                projectType: 'server',
                prevView: 'serverOptions',
                ipAddress: selectedProject.ipAddress
              });
            } else {
              // Fallback to projects list
              setCurrentView('projects');
              updateURL({ view: 'projects', category: selectedCategory || undefined });
            }
          }}
        />
      </Box>
    );
  }

  if (currentView === 'changedFiles' && selectedProject) {
    return (
      <Box sx={{ position: 'relative', minHeight: '100vh', background: 'linear-gradient(135deg, #1a2027 0%, #2d3748 100%)' }}>
        <LogoutButton onLogout={handleLogout} />
        <ChangedFilesList
          project={selectedProject}
          onBack={() => {
            // Go back to the appropriate dashboard based on prevView
            if (previousView === 'robotOptions') {
              setCurrentView('robotDashboard');
              updateURL({
                view: 'robotDashboard',
                category: selectedCategory || undefined,
                project: selectedProject.projectName,
                projectType: 'robot',
                prevView: 'robotOptions',
                ipAddress: selectedProject.ipAddress
              });
            } else if (previousView === 'serverOptions') {
              setCurrentView('serverDashboard');
              updateURL({
                view: 'serverDashboard',
                category: selectedCategory || undefined,
                project: selectedProject.projectName,
                projectType: 'server',
                prevView: 'serverOptions',
                ipAddress: selectedProject.ipAddress
              });
            } else {
              // Fallback to projects list
              setCurrentView('projects');
              updateURL({ view: 'projects', category: selectedCategory || undefined });
            }
          }}
        />
      </Box>
    );
  }

  if (currentView === 'applicationFolders' && selectedProject) {
    return (
      <Box sx={{ position: 'relative', minHeight: '100vh', background: 'linear-gradient(135deg, #1a2027 0%, #2d3748 100%)' }}>
        <LogoutButton onLogout={handleLogout} />
        <ApplicationFoldersList
          project={selectedProject}
          onBack={() => {
            // Go back to the appropriate dashboard based on prevView
            if (previousView === 'robotOptions') {
              setCurrentView('robotDashboard');
              updateURL({
                view: 'robotDashboard',
                category: selectedCategory || undefined,
                project: selectedProject.projectName,
                projectType: 'robot',
                prevView: 'robotOptions',
                ipAddress: selectedProject.ipAddress
              });
            } else if (previousView === 'serverOptions') {
              setCurrentView('serverDashboard');
              updateURL({
                view: 'serverDashboard',
                category: selectedCategory || undefined,
                project: selectedProject.projectName,
                projectType: 'server',
                prevView: 'serverOptions',
                ipAddress: selectedProject.ipAddress
              });
            } else {
              // Fallback to projects list
              setCurrentView('projects');
              updateURL({ view: 'projects', category: selectedCategory || undefined });
            }
          }}
          onFolderSelect={handleFolderSelect}
          ipAddress={selectedProject.ipAddress}
        />
      </Box>
    );
  }

  if (currentView === 'folderGitHistory' && selectedProject && selectedFolder) {
    return (
      <Box sx={{ position: 'relative', minHeight: '100vh', background: 'linear-gradient(135deg, #1a2027 0%, #2d3748 100%)' }}>
        <LogoutButton onLogout={handleLogout} />
        <FolderGitHistory
          project={selectedProject}
          folderName={selectedFolder}
          onBack={handleBackToApplicationFolders}
        />
      </Box>
    );
  }

  if (currentView === 'systemParametersGitHistory' && selectedProject) {
    return (
      <Box sx={{ position: 'relative', minHeight: '100vh', background: 'linear-gradient(135deg, #1a2027 0%, #2d3748 100%)' }}>
        <LogoutButton onLogout={handleLogout} />
        <SystemParametersGitHistory
          project={selectedProject}
          onBack={handleBackToRobotDashboard}
        />
      </Box>
    );
  }

  return null;
};

export default MainDashboard;
